/**
 * SOLID Review
 * S — Strong. One concern: the client state before anything has happened — both stores' starting data.
 * O — Good. A new store field is one value here; the store types refuse a missing one.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two operations.
 * D — Good. The Domain's phase enum, empty table (with its derived values) and dice ranking; the Application's
 *     store types.
 */
import { GamePhase } from '../../domain/uml-components/primitives';
import { ClientTable } from '../../domain/table/ClientTable';
import { DiceRanking } from '../../domain/dice/DiceRanking';
import type { TGameStoreData } from './gameStoreTypes';
import type { IStateSessionHost } from './sessionStoreTypes';

/**
 * ONE DEFINITION OF "NOTHING YET" (frontend strict-ports plan, FE6c, 2026-10-04). Until FE6c the game
 * store's starting values were spread over seven slice files in the Framework, and the Framework's port
 * holder rebuilt its null view from that store's `getInitialState()` with a hand-copied null session
 * beside it. The stores are Infrastructure's now, and the Framework may not reach into them, so the
 * starting state is declared here, where its types are, and read by both: the stores start from it,
 * and the null view (`application/ports/nullClientView.ts`) IS the view of it.
 */
export class EmptyClientState {
  /**
   * The game store's data as it starts, and as `resetAll` restores it: no identity, no room, IDLE, the
   * socket down, and the derived values of the empty table before the dice ranking has arrived — no roles,
   * no claim to offer, nothing named (frontend-domain-oo P2; until then every claim was legal here). A
   * decision that resets the table saves the derived values its own ranking gives, over these.
   * `lastGameActivityAt` is the caller's: the store stamps its module-load time and restores that same
   * stamp on every reset, as its slices did.
   */
  game(lastGameActivityAt: number): TGameStoreData {
    const data: TGameStoreData = {
      room: null,
      phase: GamePhase.IDLE,
      localUser: null,
      connectionState: 'disconnected',
      activeRollerId: null,
      activeChallengerId: null,
      activeRoundId: null,
      lastDiceRoll: null,
      minimumClaimScore: 0,
      lastClaimedScore: null,
      claimWasBlind: false,
      diceRevealed: false,
      decisionCountdown: null,
      playerLives: {},
      suspendedPlayerIds: [],
      isRestoringSession: false,
      lastGameActivityAt,
      entering: false,
      hosting: false,
      hostError: null,
      opponentClaim: null,
      revealResult: null,
      roomEnded: false,
      tableVersion: null,
      connectionWasDown: false,
      derived: ClientTable.empty().derived(DiceRanking.of([])),
    };
    return data;
  }

  /** The auth store's data as it starts: an anonymous visitor on the welcome screen, nothing pending. */
  sessionHost(): IStateSessionHost {
    const host: IStateSessionHost = {
      registered: false,
      playerId: null,
      displayName: null,
      email: null,
      matchCredits: 0,
      isGuest: false,
      screen: 'WELCOME',
      pendingEmail: null,
      pendingRoomCode: null,
      hostOnEntry: false,
      pendingJoinError: null,
      consent: null,
      ads: null,
      media: null,
      diagnostics: null,
      diceRanking: null,
    };
    return host;
  }
}
